/**
 * Fleet fuel invoices — Appwrite backend (collection: FleetFuelInvoicesSGM)
 *
 * Setup:  npm run setup:fleet-fuel-invoices
 * Used by: Admin Fuel tab in FleetMaintenanceCostTracking
 */

import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_FLEET_FUEL_INVOICES
} from '../config/appwrite';
import { ID, Query } from 'appwrite';

const LIST_PAGE_SIZE = 100;
const LIST_MAX_PAGES = 50;

const toNumber = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const calcFuelInvoiceTotal = (fuelAmount, managementFee, vat) =>
  toNumber(fuelAmount) + toNumber(managementFee) + toNumber(vat);

/** Map Appwrite document → fuel invoice row used by the UI. */
export const formatFleetFuelInvoice = (doc) => ({
  id: doc.$id ?? doc.id,
  invoiceNo: doc.invoiceNo || '',
  month: doc.month || '',
  year: doc.year || '',
  project: doc.project || '',
  fuelAmount: toNumber(doc.fuelAmount, 0),
  managementFee: toNumber(doc.managementFee, 0),
  vat: toNumber(doc.vat, 0),
  totalInvoiceAmount: toNumber(
    doc.totalInvoiceAmount,
    calcFuelInvoiceTotal(doc.fuelAmount, doc.managementFee, doc.vat)
  ),
  po: doc.po || '',
  paymentStatus: doc.paymentStatus || '',
  datePaid: doc.datePaid || '',
  prNoJazzcom: doc.prNoJazzcom || '',
  sortOrder: toNumber(doc.sortOrder, 0)
});

/** Normalize fields for Appwrite create/update. */
export const toFleetFuelInvoicePayload = (fields) => {
  const fuelAmount = toNumber(fields.fuelAmount, 0);
  const managementFee = toNumber(fields.managementFee, 0);
  const vat = toNumber(fields.vat, 0);
  return {
    invoiceNo: String(fields.invoiceNo || '').trim(),
    month: String(fields.month || '').trim(),
    year: String(fields.year || '').trim(),
    project: String(fields.project || '').trim(),
    fuelAmount,
    managementFee,
    vat,
    totalInvoiceAmount: calcFuelInvoiceTotal(fuelAmount, managementFee, vat),
    po: String(fields.po || '').trim(),
    paymentStatus: String(fields.paymentStatus || '').trim(),
    datePaid: String(fields.datePaid || '').trim(),
    prNoJazzcom: String(fields.prNoJazzcom || '').trim(),
    sortOrder: Math.round(toNumber(fields.sortOrder, 0))
  };
};

export const getFleetFuelInvoiceErrorMessage = (error) => {
  if (!error) return 'Unknown error';
  const msg = error.message || error.response?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  if (error.code === 404) {
    return 'Fuel invoices collection not found. Run: npm run setup:fleet-fuel-invoices';
  }
  if (error.code) return `Error ${error.code}`;
  return 'Request failed';
};

const listAllDocuments = async (extraQueries = []) => {
  const all = [];
  let cursor = null;

  for (let page = 0; page < LIST_MAX_PAGES; page += 1) {
    const queries = [
      Query.limit(LIST_PAGE_SIZE),
      Query.orderAsc('sortOrder'),
      ...extraQueries
    ];
    if (cursor) queries.push(Query.cursorAfter(cursor));

    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_FLEET_FUEL_INVOICES,
      queries
    );
    const docs = response.documents || [];
    all.push(...docs.map(formatFleetFuelInvoice));
    if (docs.length < LIST_PAGE_SIZE) break;
    cursor = docs[docs.length - 1].$id;
  }

  return all;
};

/** List all fleet fuel invoices (sorted by sortOrder). */
export const fetchAllFleetFuelInvoices = async () => listAllDocuments();

/** List invoices for one project (exact match). */
export const fetchFleetFuelInvoicesByProject = async (project) => {
  const p = String(project || '').trim();
  if (!p || p === 'all') return fetchAllFleetFuelInvoices();
  return listAllDocuments([Query.equal('project', p)]);
};

/** Fetch one invoice by document id. */
export const fetchFleetFuelInvoiceById = async (id) => {
  if (!id) throw new Error('Invoice id is required.');
  const doc = await databases.getDocument(
    DATABASE_ID,
    COLLECTION_ID_FLEET_FUEL_INVOICES,
    id
  );
  return formatFleetFuelInvoice(doc);
};

/** Create a fuel invoice row. */
export const createFleetFuelInvoice = async (fields) => {
  const payload = toFleetFuelInvoicePayload(fields);
  const created = await databases.createDocument(
    DATABASE_ID,
    COLLECTION_ID_FLEET_FUEL_INVOICES,
    ID.unique(),
    payload
  );
  return formatFleetFuelInvoice(created);
};

/** Update a fuel invoice row (full row payload). */
export const updateFleetFuelInvoice = async (id, fields) => {
  if (!id) throw new Error('Invoice id is required.');
  const payload = toFleetFuelInvoicePayload(fields);
  const updated = await databases.updateDocument(
    DATABASE_ID,
    COLLECTION_ID_FLEET_FUEL_INVOICES,
    id,
    payload
  );
  return formatFleetFuelInvoice(updated);
};

/** Patch selected fields on an invoice. */
export const patchFleetFuelInvoice = async (id, partialFields) => {
  if (!id) throw new Error('Invoice id is required.');
  const existing = await fetchFleetFuelInvoiceById(id);
  return updateFleetFuelInvoice(id, { ...existing, ...partialFields });
};

/** Delete a fuel invoice row. */
export const deleteFleetFuelInvoice = async (id) => {
  if (!id) throw new Error('Invoice id is required.');
  await databases.deleteDocument(DATABASE_ID, COLLECTION_ID_FLEET_FUEL_INVOICES, id);
  return true;
};
